import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

const FAQ_DATA = [
  {
    category: "Getting Help",
    questions: [
      { q: "How do I get help from The Pillar Project?", a: "Contact us through our Get Support form, email director@thepillarprojectbaltimore.org, or have a caseworker, attorney, or police officer submit a referral through our Partner Portal. We respond as quickly as possible and follow up in the way that is safest for you." },
      { q: "What types of violence do you respond to?", a: "Domestic violence, intimate partner violence, human trafficking, sexual assault, and community violence. We serve survivors of any age, background, or immigration status." },
      { q: "Is there a cost for your services?", a: "No. All services The Pillar Project provides directly are free and confidential. Referral partners may have their own eligibility rules." },
      { q: "Do I need a police report or court order to get help?", a: "No. You do not need any documentation to reach out. We help anyone who contacts us, whether or not a report has been filed." },
      { q: "Can I get help if I am not a U.S. citizen?", a: "Yes. We serve survivors regardless of immigration status. All contact is confidential." },
    ]
  },
  {
    category: "Our Services",
    questions: [
      { q: "What happens when I first contact you?", a: "We listen and help identify your immediate needs and goals. Depending on your situation, we may provide safety-planning support, coordinate transportation or essential items when resources are available, and provide warm referrals to housing, mental-health, legal, or specialized survivor-service providers. We follow up while you navigate waiting lists and appointments." },
      { q: "Does The Pillar Project operate its own shelter?", a: "Not at this time. We help survivors identify and connect with available shelter and housing resources through established community relationships. Developing survivor housing and transitional-housing capacity is part of our long-term vision." },
      { q: "Does The Pillar Project provide legal representation or clinical counseling?", a: "Not at this time. We refer survivors to trusted legal-services and mental-health providers and help prepare them for those appointments. In-house legal and mental-health services are part of our long-term vision." },
      { q: "What is the Lethality Assessment Program (LAP)?", a: "LAP is a coordinated partnership with law-enforcement partners. When officers respond to a domestic violence call, they can submit an assessment to us so we can follow up with the survivor and coordinate referrals." },
      { q: "What workforce and training programs do you offer today?", a: "Job readiness (resume, interview prep, professional development, work-appropriate clothing coordination, employer referrals) and hospitality-industry pathways including ServSafe certification when scheduled. Additional healthcare and entrepreneurship pathways are part of our long-term vision." },
    ]
  },
  {
    category: "For Partners and Agencies",
    questions: [
      { q: "How do I refer a survivor?", a: "Register on our Partner Portal at thepillarprojectbaltimore.org/partner/login. From there, you can submit referrals, submit LAP reports, and track the status of your submissions." },
      { q: "Who can use the Partner Portal?", a: "Police departments, CPS/DSS caseworkers, court and probation officers, hospital social workers, school counselors, legal aid attorneys, and community organizations." },
      { q: "How can my organization partner with The Pillar Project?", a: "Email director@thepillarprojectbaltimore.org. We welcome partnerships with agencies, businesses, and organizations that serve survivors — including referral pipelines, co-hosted trainings, and coordinated service delivery." },
      { q: "Do you offer trainings for law enforcement or community organizations?", a: "Yes. We deliver trauma-informed trainings on identifying human trafficking indicators, responding to domestic violence, and survivor-centered intervention. Request a training from our Professional Training page." },
    ]
  },
  {
    category: "Donations and Volunteering",
    questions: [
      { q: "How can I donate?", a: "Visit thepillarprojectbaltimore.org/donate. We accept one-time and recurring donations. The Pillar Project Corporation is a registered 501(c)(3) and all donations are tax-deductible to the extent permitted by law." },
      { q: "Can I volunteer?", a: "Yes. Volunteer roles include community outreach, event support, resume and interview coaching, professional-clothing coordination, and administrative help. Volunteers may be asked to complete an application, interview, background screening, confidentiality agreement, and role-specific training. Visit our Careers page or email director@thepillarprojectbaltimore.org." },
    ]
  },
];

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-warm-800 text-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-medium tracking-tight leading-tight mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }} data-testid="faq-hero-title">
              Frequently Asked Questions
            </h1>
            <p className="text-warm-200 text-lg">
              Answers about our services, how to get help, and how to work with us.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6">
          {FAQ_DATA.map((category, catIdx) => (
            <motion.div key={category.category} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} className="mb-10">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200"
                style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                {category.category}
              </h2>
              <div className="space-y-2">
                {category.questions.map((faq, faqIdx) => {
                  const itemId = `${catIdx}-${faqIdx}`;
                  return (
                    <div key={itemId} className="border border-gray-100 rounded-lg overflow-hidden" data-testid={`faq-item-${catIdx}-${faqIdx}`}>
                      <button onClick={() => toggleItem(itemId)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors">
                        <span className="font-medium text-gray-900 text-sm pr-4">{faq.q}</span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openItems[itemId] ? 'rotate-180' : ''}`} />
                      </button>
                      {openItems[itemId] && (
                        <div className="px-5 pb-4">
                          <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-warm-800 text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            Still have questions?
          </h2>
          <p className="text-warm-200 mb-6">Reach out directly. We respond within 24 hours.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-warm-800 hover:bg-warm-100 rounded-full px-8 py-5 font-semibold">
                Contact Us <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:1-800-799-7233">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-5">
                DV Hotline: 1-800-799-7233
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
